import React, { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useIsMobile } from "../../hooks/responsive";
import BottomSheet from "./BottomSheet";

const maxWidthMap = {
  xs: "20rem",
  sm: "24rem",
  md: "28rem",
  lg: "32rem",
  xl: "36rem",
  "2xl": "42rem",
  "3xl": "48rem",
  "4xl": "56rem",
  "5xl": "64rem",
  "6xl": "72rem",
  "7xl": "80rem",
  full: "100%",
  min: "min-content",
  max: "max-content",
  fit: "fit-content",
  prose: "65ch",
  "500px": "500px",
  "600px": "600px",
};

const Modal = ({
  children,
  onClose,
  title,
  headerContent,
  footerContent,
  maxWidth = "2xl",
  showCloseButton = true,
  position = "center",
  zIndex = 100,
  customRef,
  mobileFullscreen = false,
  preventOutsideClick = false,
  headerPadding = "tw-p-6",
  bodyPadding = "tw-px-6 tw-py-4",
  open,
  snapPoints,
  forceDesktopView = false,
  maxHeight = "90vh",
}) => {
  const internalRef = useRef(null);
  const ref = customRef || internalRef;
  const isMobile = useIsMobile();
  const actualMaxWidth = maxWidthMap[maxWidth] || maxWidth;

  useEffect(() => {
    if ((!isMobile || forceDesktopView) && open) {
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !preventOutsideClick
        ) {
          onClose();
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [onClose, ref, preventOutsideClick, isMobile, forceDesktopView, open]);

  if (!open) return null;

  if (isMobile && !forceDesktopView) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        title={title}
        headerContent={headerContent}
        footerContent={footerContent}
        showCloseButton={showCloseButton}
        zIndex={zIndex}
        customRef={ref}
        headerPadding={headerPadding}
        bodyPadding={bodyPadding}
        snapPoints={snapPoints}
      >
        {children}
      </BottomSheet>
    );
  }

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4"
      style={{ zIndex }}
    >
      <div
        className="tw-fixed tw-inset-0 tw-bg-black/50"
        onClick={preventOutsideClick ? undefined : onClose}
        style={{ backdropFilter: "blur(5px)", zIndex }}
      />
      <div
        ref={ref}
        className="tw-bg-white tw-shadow-2xl tw-w-full tw-rounded-3xl tw-flex tw-flex-col tw-overflow-hidden"
        style={{
          zIndex: zIndex + 1,
          maxWidth: actualMaxWidth,
          height: maxHeight,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || headerContent || showCloseButton) && (
          <div
            className={`${headerPadding} tw-flex tw-justify-between tw-items-start tw-flex-shrink-0 tw-border-b tw-border-gray-100`}
          >
            <div className="tw-w-full">
              {title && (
                <h2 className="tw-text-2xl tw-font-bold tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-transparent">
                  {title}
                </h2>
              )}
              {headerContent}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="tw-p-2 tw-rounded-full hover:tw-bg-gray-100"
              >
                <X className="tw-w-6 tw-h-6 tw-text-gray-500" />
              </button>
            )}
          </div>
        )}

        <div
          className={`${bodyPadding} tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent`}
        >
          {children}
        </div>

        {footerContent && (
          <div className="tw-p-6 tw-border-t tw-border-gray-100 tw-flex-shrink-0">
            {footerContent}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
